<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter, type RouteLocationNormalized } from 'vue-router'
import { useJwtStore, usePermissionsStore } from '@/stores'
import { useVersionStore } from '@/stores/version'
import { useLocale } from '@/composables/useLocale'
import { useAxios } from '@/composables/useAxios'
import DashboardConfirmDialog from '@/components/dashboard/common/DashboardConfirmDialog.vue'
import { DIRTY_FORM_NAVIGATION_EVENT, type DirtyFormNavigationEvent } from '@/router'

const router = useRouter()
const jwtStore = useJwtStore()
const versionStore = useVersionStore()
const permissionsStore = usePermissionsStore()
const { initLocaleFromAccount } = useLocale()

// Dirty form navigation state
const showUnsavedChangesDialog = ref(false)
const pendingNavigation = ref<RouteLocationNormalized | null>(null)

// Handle dirty form navigation event from router guard
const handleDirtyFormNavigation = (event: CustomEvent<DirtyFormNavigationEvent>) => {
  pendingNavigation.value = event.detail.to
  showUnsavedChangesDialog.value = true
}

// User confirmed: discard changes and navigate
const handleDiscardChanges = () => {
  versionStore.clearAllDirtyForms()
  showUnsavedChangesDialog.value = false

  if (pendingNavigation.value) {
    router.push(pendingNavigation.value)
    pendingNavigation.value = null
  }
}

// User cancelled: stay on current page
const handleStayOnPage = () => {
  showUnsavedChangesDialog.value = false
  pendingNavigation.value = null
}

onMounted(async () => {
  // Start watching JWT for removal/expiration - redirects to login if JWT becomes invalid
  jwtStore.startJwtWatcher()
  // Start watching for new app versions
  versionStore.startVersionWatcher()

  // Listen for dirty form navigation events
  window.addEventListener(DIRTY_FORM_NAVIGATION_EVENT, handleDirtyFormNavigation as (e: Event) => void)

  // Initialize permissions from JWT if already logged in
  if (jwtStore.getJwt) {
    const jwtContent = jwtStore.getJwtContent
    if (jwtContent) permissionsStore.initFromJWT(jwtContent)

    // Fetch delegations and manageable domains
    await Promise.all([
      permissionsStore.fetchDelegations(),
      permissionsStore.fetchManageableDomains()
    ])
  }

  // Initialize locale from account preferences
  try {
    const res = await useAxios<null, { lang?: string }>('GET', '/account', true)
    if (res?.data?.lang) {
      initLocaleFromAccount(res.data.lang)
    }
  } catch {
    // Fallback to browser detection if account fetch fails
    initLocaleFromAccount()
  }
})

onUnmounted(() => {
  jwtStore.stopJwtWatcher()
  versionStore.stopVersionWatcher()

  // Clean up event listener
  window.removeEventListener(DIRTY_FORM_NAVIGATION_EVENT, handleDirtyFormNavigation as (e: Event) => void)
})

</script>

<template>
  <div class="dashboard-layout">
    <!-- Background decoration -->
    <div class="bg-decoration">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>

    <slot />

    <!-- Unsaved changes confirmation dialog -->
    <DashboardConfirmDialog
      v-model="showUnsavedChangesDialog"
      title="Ungespeicherte Änderungen"
      description="Du hast ungespeicherte Änderungen. Wenn du jetzt navigierst, gehen diese verloren."
      variant="warning"
      confirm-text="Verwerfen"
      confirm-icon="mdi-delete-outline"
      cancel-text="Abbrechen"
      :persistent="true"
      @confirm="handleDiscardChanges"
      @cancel="handleStayOnPage"
    />
  </div>
</template>

<style lang="scss" scoped>
.dashboard-layout {
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0f1e 0%, #0d1528 50%, #0a1220 100%);
  position: relative;
  overflow-x: hidden;
}

/* Background decoration */
.bg-decoration {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.4;

  &.orb-1 {
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(0, 176, 255, 0.15) 0%, transparent 70%);
    top: -200px;
    left: -200px;
  }

  &.orb-2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(31, 226, 212, 0.1) 0%, transparent 70%);
    bottom: -150px;
    right: -150px;
  }

  &.orb-3 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(120, 163, 255, 0.08) 0%, transparent 70%);
    top: 40%;
    left: 30%;
  }
}
</style>
